import type { RowData } from "@tanstack/react-table";
import type { DataGridColumnState } from "../types";
import type { DataGridColumn, DataGridColumnKind, DataGridPin } from "./types";
import { DEFAULT_VARIANT_KEY } from "@helpers/viewColumnVariant";

const DISPLAY_KINDS: ReadonlySet<DataGridColumnKind> = new Set([
    "select",
    "expand",
    "dragHandle",
    "actions",
    "rowNumber",
]);

/** Select, expand, drag handle, actions, and row number: columns without a value. */
export const isDisplayColumnKind = (kind: DataGridColumnKind | undefined) =>
    kind !== undefined && DISPLAY_KINDS.has(kind);

/** What layout code needs from a leaf column definition. */
export interface DataGridLeafColumnInfo {
    id: string;
    /** Display columns stay where the definitions put them. */
    isDisplay: boolean;
    pin: DataGridPin | undefined;
    /** With `variants`: its `customVariants` key in a saved view, and the values it offers. */
    variants?: { key: string; values: readonly string[] };
}

/**
 * A leaf definition's column id by TanStack's rule: `id`, else `accessorKey`
 * with dots as underscores, else a string `header`.
 */
export const getColumnDefId = <TData extends RowData>(
    columnDef: DataGridColumn<TData>,
): string | undefined => {
    if (columnDef.id !== undefined) {
        return columnDef.id;
    }
    if ("accessorKey" in columnDef && columnDef.accessorKey !== undefined) {
        return String(columnDef.accessorKey).replaceAll(".", "_");
    }
    return typeof columnDef.header === "string" ? columnDef.header : undefined;
};

/** The leaf columns in definition order, groups flattened. */
export const getLeafColumnInfos = <TData extends RowData>(
    columnDefs: ReadonlyArray<DataGridColumn<TData>>,
): DataGridLeafColumnInfo[] =>
    columnDefs.flatMap((columnDef): DataGridLeafColumnInfo[] => {
        if ("columns" in columnDef && columnDef.columns) {
            return getLeafColumnInfos(columnDef.columns);
        }
        const id = getColumnDefId(columnDef);
        const variants = columnDef.meta?.variants;
        return id === undefined
            ? []
            : [
                  {
                      id,
                      isDisplay: isDisplayColumnKind(columnDef.meta?.kind),
                      pin: columnDef.meta?.pin,
                      ...(variants && {
                          variants: {
                              key: variants.optionKey ?? DEFAULT_VARIANT_KEY,
                              values: variants.options.map(
                                  (option) => option.value,
                              ),
                          },
                      }),
                  },
              ];
    });

/**
 * The layout the definitions declare: definition order, every column shown at
 * its default width, pins from `meta.pin`. Column settings reset to it.
 */
export const getDefaultColumnState = (
    leaves: readonly DataGridLeafColumnInfo[],
): DataGridColumnState => ({
    order: leaves.map((leaf) => leaf.id),
    sizing: {},
    visibility: {},
    pinning: {
        start: leaves
            .filter((leaf) => leaf.pin === "start")
            .map(({ id }) => id),
        end: leaves.filter((leaf) => leaf.pin === "end").map(({ id }) => id),
    },
    variants: {},
});

/**
 * The default column settings compare against and Reset restores: the
 * declared parts, with the definitions' defaults for the rest. No `sizing`
 * means no width overrides.
 */
export const resolveDefaultColumnState = ({
    leaves,
    declared,
}: {
    leaves: readonly DataGridLeafColumnInfo[];
    declared: Partial<DataGridColumnState> | undefined;
}): DataGridColumnState => {
    const definitionDefault = getDefaultColumnState(leaves);
    return {
        order: declared?.order ?? definitionDefault.order,
        sizing: declared?.sizing ?? definitionDefault.sizing,
        visibility: declared?.visibility ?? definitionDefault.visibility,
        pinning: declared?.pinning ?? definitionDefault.pinning,
        variants: declared?.variants ?? definitionDefault.variants,
    };
};

const isSameList = (a: readonly string[], b: readonly string[]) =>
    a.length === b.length && a.every((item, index) => item === b[index]);

const isSameRecord = <TValue>(
    a: Readonly<Record<string, TValue>>,
    b: Readonly<Record<string, TValue>>,
) => {
    const keys = Object.keys(a);
    return (
        keys.length === Object.keys(b).length &&
        keys.every((key) => key in b && a[key] === b[key])
    );
};

/** Value equality, so adapters can keep one object while nothing changed. */
export const isSameColumnState = (
    a: DataGridColumnState,
    b: DataGridColumnState,
) =>
    isSameList(a.order, b.order) &&
    isSameRecord(a.sizing, b.sizing) &&
    isSameRecord(a.visibility, b.visibility) &&
    isSameList(a.pinning.start, b.pinning.start) &&
    isSameList(a.pinning.end, b.pinning.end) &&
    isSameRecord(a.variants, b.variants);
